import { useState } from "react"
import { cn } from "@/lib/cn"
import type { ConceptSummary, GraphNode, GraphPayload } from "@/types/knowledgeGraph"
import { GraphCanvas } from "./GraphCanvas"

export interface CommodityGraphSnapshot {
  present: boolean
  concept: ConceptSummary | null
  graph: GraphPayload
  documents: GraphNode[]
  related: ConceptSummary[]
}

interface CommodityGraphPanelProps {
  snapshot: CommodityGraphSnapshot
  commodityName: string
  explorerPath: string
  conceptPathPrefix?: string
}

function DocumentRow({ document }: { document: GraphNode }) {
  const label = document.source === "youtube" ? "Video" : "Report"
  const title = document.title ?? document.url ?? `Source #${document.id}`
  const meta = [document.channel_title, document.published_at?.slice(0, 10)].filter(Boolean).join(" · ")

  return (
    <li className="flex items-start gap-2 py-2">
      <span className="mt-0.5 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600">
        {label}
      </span>
      <div className="min-w-0">
        {document.external_url ? (
          <a href={document.external_url} className="block truncate text-sm text-indigo-600 hover:underline">
            {title}
          </a>
        ) : (
          <span className="block truncate text-sm text-gray-900">{title}</span>
        )}
        {meta && <span className="text-xs text-gray-500">{meta}</span>}
      </div>
    </li>
  )
}

/**
 * Quick view of a commodity's graph neighbourhood on its hub page.
 *
 * The list of sources is what most visitors want ("which videos discussed natural gas
 * this week?"), so it renders immediately; the canvas is behind a toggle because
 * laying out a graph is not worth the work on a page nobody scrolled to.
 */
export function CommodityGraphPanel({
  snapshot,
  commodityName,
  explorerPath,
  conceptPathPrefix,
}: CommodityGraphPanelProps) {
  const [showGraph, setShowGraph] = useState(false)

  const conceptHref = (slug: string) =>
    conceptPathPrefix ? `${conceptPathPrefix}/${slug}` : `${explorerPath}?slug=${slug}`

  if (!snapshot.present || snapshot.graph.nodes.length === 0) {
    return (
      <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
        <h2 className="text-xl font-semibold text-gray-900">Knowledge graph</h2>
        <p className="mt-2 text-gray-500">
          Nothing linked to {commodityName} yet. Research is added as videos and reports are
          ingested.
        </p>
      </div>
    )
  }

  return (
    <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold text-gray-900">Knowledge graph</h2>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowGraph((value) => !value)}
            aria-expanded={showGraph}
            className={cn(
              "rounded px-2 py-1 text-xs font-medium",
              showGraph
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            {showGraph ? "Hide map" : "Show map"}
          </button>
          <a
            href={`${explorerPath}?slug=${snapshot.concept?.slug ?? ""}`}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
          >
            Open in explorer
          </a>
        </div>
      </div>

      {showGraph && (
        <div className="mb-4 h-72 overflow-hidden rounded-lg">
          <GraphCanvas
            graph={snapshot.graph}
            selectedId={snapshot.concept?.id ?? null}
            onSelect={() => undefined}
            onExpand={() => undefined}
          />
        </div>
      )}

      {snapshot.related.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Connected concepts
          </h3>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {snapshot.related.map((concept) => (
              <li key={concept.id}>
                <a
                  href={conceptHref(concept.slug ?? "")}
                  className="inline-block rounded bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200 hover:text-gray-900"
                >
                  {concept.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {snapshot.documents.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Mentioned in
          </h3>
          <ul className="mt-1 divide-y divide-gray-100">
            {snapshot.documents.map((document) => (
              <DocumentRow key={document.id} document={document} />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
